import { useContext, useState, useEffect } from "react"
import { Field, useFormikContext } from "formik"
import { UserContext } from "../../context/userContext"
import { FormContext } from "../../context/formContext"
import { InputText as Input } from "primereact/inputtext"
import { useLazyQuery } from "@apollo/client"
import { IS_PROGRAM_ID_NAME_UNIQUE } from "./queries/queries"
import { TRIM_TYPES, trimTextValue } from "./helpers/trimTextValue"
import styles from "../../styles/Form.module.css"

export default function TypeProgramIdName(props) {
  const { field, index, initialValue } = props
  // Convert the "true" value to boolean, in case it's a string
  const isFieldRequired =
    field.field_validation?.required &&
    field.field_validation.required[0] === "true"

  const formik = useFormikContext()
  const userCtx = useContext(UserContext)
  const form = useContext(FormContext)
  const [validMessage, setValidMessage] = useState(null)
  const [isChecking, setIsChecking] = useState(false)

  const { enterprise_id, organization_id } = userCtx.session.user

  const program_identity_id =
    form.editMode && formik.values.program_identity_id
      ? String(formik.values.program_identity_id)
      : null

  const organization_id_formik =
    form.organization_id ||
    (formik.values.multi_organization_ids_by_program &&
      formik.values.multi_organization_ids_by_program.length > 0
      ? formik.values.multi_organization_ids_by_program[0].key
      : null)

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (form.editMode && formik.values.program_id_name === initialValue) {
      setValidMessage(null)
    }
  }, [form.editMode, formik.values.program_id_name, initialValue])

  const [checkProgramNameUnique, { error }] = useLazyQuery(
    IS_PROGRAM_ID_NAME_UNIQUE,
    {
      fetchPolicy: "network-only",
      onCompleted: () => setIsChecking(false),
    }
  )

  const handleBlur = async (e) => {
    const currentProgramName = trimTextValue(e.target.value, TRIM_TYPES.BOTH)
    if (e.target.value !== currentProgramName) {
      formik.setFieldValue(field.field_name, currentProgramName, true)
    }
    formik.handleBlur(e)

    if (!currentProgramName || currentProgramName === initialValue) {
      return
    }

    setIsChecking(true)
    try {
      const response = await checkProgramNameUnique({
        variables: {
          enterprise_id: enterprise_id,
          program_id_name: currentProgramName,
          organization_id: form.editMode
            ? organization_id_formik
            : organization_id,
          program_identity_id: program_identity_id,
        },
      })

      const isUnique = response?.data?.isUniqueProgramIdName

      if (!isUnique) {
        formik.setFieldError(
          "program_id_name",
          "This Program name already exists. Please provide a unique name."
        )
        setValidMessage(null)
      } else {
        if (formik.errors.program_id_name) {
          formik.setFieldError("program_id_name", undefined)
        }
        setValidMessage("The program ID name is valid.")
      }
    } catch (err) {
      console.error("Error verifying program ID name uniqueness:", err)
      formik.setFieldError(
        "program_id_name",
        "Error checking the program ID name."
      )
      setValidMessage(null)
    } finally {
      setIsChecking(false)
    }
  }

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: "block",
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isFieldRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => (
            <>
              <Input
                name={formikField.name}
                id={formikField.name}
                value={formikField.value}
                onChange={formikField.onChange}
                disabled={isChecking || Boolean(field.read_only)}
                onBlur={handleBlur}
                autoComplete="off"
              />
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
              {isChecking && (
                <div className={styles.infoFieldMsg}>Checking uniqueness...</div>
              )}
              {error && (
                <div className={styles.errFieldMsg}>
                  Error checking the program ID name.
                </div>
              )}
              {!isChecking && !meta.error && validMessage && (
                <div className={styles.validFieldMsg}>{validMessage}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}
